import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import RichTextEditor from '../components/RichTextEditor';
import VideoUploadModal from '../components/VideoUploadModal';
import VideoPlayer from '../components/VideoPlayer';
import { Save, X } from 'lucide-react';
import { portalSettingsService } from '../services/portalSettingsService';
import { projectService } from '../services/projectService';
import { Project } from '../types/project';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/PortalSettings.css';

const PortalSettings: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'units' | 'parking' | 'incentives' | 'payments' | 'others'>('general');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);

  // Track which images should be deleted
  const [imagesToDelete, setImagesToDelete] = useState<Set<string>>(new Set());

  // Settings state
  const [settings, setSettings] = useState({
    // General
    primaryColor: '#891F1E',
    headerLogo: null as File | null,
    headerLogoUrl: '',
    loginPageLogo: null as File | null,
    loginPageLogoUrl: '',
    loginPageHeroImage: null as File | null,
    loginPageHeroImageUrl: '',
    loginPageVideoUrl: '',
    loginPageVideoType: '' as 'youtube' | 'vimeo' | 'mp4' | '',
    introText: '',
    bannerImage: null as File | null,
    bannerImageUrl: '',
    extraInfoTitle: 'Welcome to your Edenshaw Purchaser Portal.',
    extraInfoBody: `BUILDING BEYOND EXPECTATION\n\nWe believe buying a home is about more than just investing in a property: it's about building a long-lasting connection to a community. Inspired by the meticulously executed designs and fine craftsmanship of Haida master-woodcarver Charles Edenshaw, we craft homes for modern living, while paying homage to our past and looking towards the future.`,
    helpText: 'For any help please contact us.\n\nPhone: 905-890-9500\n\nEmail: customercare@edenshaw.com',
    showImageDisclaimer: false,
    imageDisclaimerText: '',

    // Units
    unitDisplay: {
      model: true,
      collection: true,
      unitType: true,
      bathroom: true,
      bedroom: true,
      den: true,
      media: false,
      flex: false,
      area: true,
      ceiling: false,
      outdoorArea: true,
      outdoorType: true,
      parking: false,
      locker: false,
      bicycle: false
    },
    unitOption: {
      floorLayout: true,
      keyMap: false,
      electricalLayout: true,
      suiteLayout: true,
      photos: true
    },
    unitSummary: {
      model: true,
      collection: false,
      unitType: false,
      bathroom: false,
      bedroom: true,
      den: false,
      media: true,
      flex: false,
      area: true,
      ceiling: false,
      outdoorArea: false,
      outdoorType: false,
      floorMarketing: false,
      views: false
    },

    // Parking
    parkingSelectionTimeLimit: 5,
    parkingInfoText: '',
    lockerInfoText: '',
    bicycleInfoText: '',

    // Incentives / Promotions
    displayIncentivesOnHomePage: false,
    displayIncentivesOnUnitDetails: false,
    incentiveTitle: '',
    incentiveBody: '',
    incentiveImage: null as File | null,
    incentiveImageUrl: '',

    // Payments
    showPaymentInstructions: false,
    paymentInstructions: '',

    // Others
    showDesignStudioSubmitInstructions: false,
    designStudioSubmitInstructions: '',
    showLayoutCustomizationSubmitInstructions: false,
    layoutCustomizationSubmitInstructions: '',
    homeOwnersGuideProjects: [] as string[],
    colorSelectionProjects: [] as string[],
    amenitiesButtonProjects: [] as string[],
    projectProgressButtonProjects: [] as string[],
    closingGuideButtonProjects: [] as Array<{ projectId: string; url: string }>
  });

  useEffect(() => {
    if (workspaceId) {
      loadSettings();
      loadProjects();
    }
  }, [workspaceId]);

  const loadProjects = async () => {
    if (!workspaceId) return;

    try {
      const data = await projectService.getAll(workspaceId);
      setProjects(data.projects || []);
    } catch (err: any) {
      console.error('Error loading projects:', err);
      // Don't show error toast for projects - it's not critical
    }
  };

  const loadSettings = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const data = await portalSettingsService.get(workspaceId);

      // Update settings state with loaded data
      setSettings({
        primaryColor: data.primaryColor,
        headerLogo: null,
        headerLogoUrl: data.headerLogo || '',
        loginPageLogo: null,
        loginPageLogoUrl: data.loginPageLogo || '',
        loginPageHeroImage: null,
        loginPageHeroImageUrl: data.loginPageHeroImage || '',
        loginPageVideoUrl: data.loginPageVideoUrl || '',
        loginPageVideoType: data.loginPageVideoType || '',
        bannerImage: null,
        bannerImageUrl: data.bannerImage || '',
        introText: data.introText || '',
        extraInfoTitle: data.extraInfoTitle,
        extraInfoBody: data.extraInfoBody,
        helpText: data.helpText,
        showImageDisclaimer: data.showImageDisclaimer || false,
        imageDisclaimerText: data.imageDisclaimerText || '',
        unitDisplay: data.unitDisplay || {
          model: true,
          collection: true,
          unitType: true,
          bathroom: true,
          bedroom: true,
          den: true,
          media: false,
          flex: false,
          area: true,
          ceiling: false,
          outdoorArea: true,
          outdoorType: true,
          parking: false,
          locker: false,
          bicycle: false
        },
        unitOption: data.unitOption || {
          floorLayout: true,
          keyMap: false,
          electricalLayout: true,
          suiteLayout: true,
          photos: true
        },
        unitSummary: data.unitSummary || {
          model: true,
          collection: false,
          unitType: false,
          bathroom: false,
          bedroom: true,
          den: false,
          media: true,
          flex: false,
          area: true,
          ceiling: false,
          outdoorArea: false,
          outdoorType: false,
          floorMarketing: false,
          views: false
        },
        parkingSelectionTimeLimit: data.parkingSelectionTimeLimit || 5,
        parkingInfoText: data.parkingInfoText || '',
        lockerInfoText: data.lockerInfoText || '',
        bicycleInfoText: data.bicycleInfoText || '',
        displayIncentivesOnHomePage: data.displayIncentivesOnHomePage || false,
        displayIncentivesOnUnitDetails: data.displayIncentivesOnUnitDetails || false,
        incentiveTitle: data.incentiveTitle || '',
        incentiveBody: data.incentiveBody || '',
        incentiveImage: null,
        incentiveImageUrl: data.incentiveImage || '',
        showPaymentInstructions: data.showPaymentInstructions || false,
        paymentInstructions: data.paymentInstructions || '',
        showDesignStudioSubmitInstructions: data.showDesignStudioSubmitInstructions || false,
        designStudioSubmitInstructions: data.designStudioSubmitInstructions || '',
        showLayoutCustomizationSubmitInstructions: data.showLayoutCustomizationSubmitInstructions || false,
        layoutCustomizationSubmitInstructions: data.layoutCustomizationSubmitInstructions || '',
        homeOwnersGuideProjects: data.homeOwnersGuideProjects || [],
        colorSelectionProjects: data.colorSelectionProjects || [],
        amenitiesButtonProjects: data.amenitiesButtonProjects || [],
        projectProgressButtonProjects: data.projectProgressButtonProjects || [],
        closingGuideButtonProjects: data.closingGuideButtonProjects || [],
      });
    } catch (err: any) {
      console.error('Error loading portal settings:', err);
      // If no settings exist yet, that's okay - we'll use defaults
      if (err.response?.status !== 404) {
        toast.error('Failed to load portal settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleFileChange = (field: string, file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings({
          ...settings,
          [field]: file,
          [`${field}Url`]: reader.result as string
        });
        // Remove from delete list if user uploads new file
        const newImagesToDelete = new Set(imagesToDelete);
        newImagesToDelete.delete(field);
        setImagesToDelete(newImagesToDelete);
      };
      reader.readAsDataURL(file);
    } else {
      // User wants to delete the image - track it
      const urlField = `${field}Url`;
      const hasExistingImage = (settings as any)[urlField];

      setSettings({
        ...settings,
        [field]: null,
        [`${field}Url`]: ''
      });

      // Only track for deletion if there was an existing image
      if (hasExistingImage) {
        const newImagesToDelete = new Set(imagesToDelete);
        newImagesToDelete.add(field);
        setImagesToDelete(newImagesToDelete);
      }
    }
  };

  const handleDeleteVideo = async () => {
    if (!workspaceId) return;

    try {
      setSaving(true);

      // Save empty values to clear the video
      await portalSettingsService.save(workspaceId, {
        loginPageVideoUrl: '',
        loginPageVideoType: undefined
      });

      // Update local state
      setSettings({
        ...settings,
        loginPageVideoUrl: '',
        loginPageVideoType: ''
      });

      toast.success('Video removed successfully');
    } catch (error: any) {
      console.error('Error removing video:', error);
      toast.error(error.response?.data?.message || 'Failed to remove video');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!workspaceId) return;

    try {
      setSaving(true);

      // Build form data based on active tab
      let formData: any = {};

      if (activeTab === 'general') {
        formData = {
          primaryColor: settings.primaryColor,
          headerLogo: settings.headerLogo || undefined,
          loginPageLogo: settings.loginPageLogo || undefined,
          loginPageHeroImage: settings.loginPageHeroImage || undefined,
          loginPageVideoUrl: settings.loginPageVideoUrl || undefined,
          loginPageVideoType: settings.loginPageVideoType || undefined,
          bannerImage: settings.bannerImage || undefined,
          introText: settings.introText,
          extraInfoTitle: settings.extraInfoTitle,
          extraInfoBody: settings.extraInfoBody,
          helpText: settings.helpText,
          showImageDisclaimer: settings.showImageDisclaimer,
          imageDisclaimerText: settings.imageDisclaimerText,
        };
      } else if (activeTab === 'units') {
        formData = {
          unitDisplay: settings.unitDisplay,
          unitOption: settings.unitOption,
          unitSummary: settings.unitSummary,
        };
      } else if (activeTab === 'parking') {
        formData = {
          parkingSelectionTimeLimit: settings.parkingSelectionTimeLimit,
          parkingInfoText: settings.parkingInfoText,
          lockerInfoText: settings.lockerInfoText,
          bicycleInfoText: settings.bicycleInfoText,
        };
      } else if (activeTab === 'incentives') {
        formData = {
          displayIncentivesOnHomePage: settings.displayIncentivesOnHomePage,
          displayIncentivesOnUnitDetails: settings.displayIncentivesOnUnitDetails,
          incentiveTitle: settings.incentiveTitle,
          incentiveBody: settings.incentiveBody,
          incentiveImage: settings.incentiveImage || undefined,
        };
      } else if (activeTab === 'payments') {
        formData = {
          showPaymentInstructions: settings.showPaymentInstructions,
          paymentInstructions: settings.paymentInstructions,
        };
      } else if (activeTab === 'others') {
        formData = {
          showDesignStudioSubmitInstructions: settings.showDesignStudioSubmitInstructions,
          designStudioSubmitInstructions: settings.designStudioSubmitInstructions,
          showLayoutCustomizationSubmitInstructions: settings.showLayoutCustomizationSubmitInstructions,
          layoutCustomizationSubmitInstructions: settings.layoutCustomizationSubmitInstructions,
          homeOwnersGuideProjects: settings.homeOwnersGuideProjects,
          colorSelectionProjects: settings.colorSelectionProjects,
          amenitiesButtonProjects: settings.amenitiesButtonProjects,
          projectProgressButtonProjects: settings.projectProgressButtonProjects,
          closingGuideButtonProjects: settings.closingGuideButtonProjects,
        };
      }

      // Add images to delete
      if (imagesToDelete.size > 0) {
        formData.imagesToDelete = Array.from(imagesToDelete);
      }

      // Save and get updated settings from response
      const updatedSettings = await portalSettingsService.save(workspaceId, formData);
      toast.success('Portal settings saved successfully');

      // Update local state with response data instead of reloading
      setSettings({
        ...settings,
        primaryColor: updatedSettings.primaryColor,
        headerLogoUrl: updatedSettings.headerLogo || '',
        loginPageLogoUrl: updatedSettings.loginPageLogo || '',
        loginPageHeroImageUrl: updatedSettings.loginPageHeroImage || '',
        loginPageVideoUrl: updatedSettings.loginPageVideoUrl || '',
        loginPageVideoType: updatedSettings.loginPageVideoType || '',
        bannerImageUrl: updatedSettings.bannerImage || '',
        incentiveImageUrl: updatedSettings.incentiveImage || '',
        introText: updatedSettings.introText || '',
        extraInfoTitle: updatedSettings.extraInfoTitle,
        extraInfoBody: updatedSettings.extraInfoBody,
        helpText: updatedSettings.helpText,
        showImageDisclaimer: updatedSettings.showImageDisclaimer || false,
        imageDisclaimerText: updatedSettings.imageDisclaimerText || '',
        unitDisplay: updatedSettings.unitDisplay || settings.unitDisplay,
        unitOption: updatedSettings.unitOption || settings.unitOption,
        unitSummary: updatedSettings.unitSummary || settings.unitSummary,
        parkingSelectionTimeLimit: updatedSettings.parkingSelectionTimeLimit || 5,
        parkingInfoText: updatedSettings.parkingInfoText || '',
        lockerInfoText: updatedSettings.lockerInfoText || '',
        bicycleInfoText: updatedSettings.bicycleInfoText || '',
        displayIncentivesOnHomePage: updatedSettings.displayIncentivesOnHomePage || false,
        displayIncentivesOnUnitDetails: updatedSettings.displayIncentivesOnUnitDetails || false,
        incentiveTitle: updatedSettings.incentiveTitle || '',
        incentiveBody: updatedSettings.incentiveBody || '',
        showPaymentInstructions: updatedSettings.showPaymentInstructions || false,
        paymentInstructions: updatedSettings.paymentInstructions || '',
        showDesignStudioSubmitInstructions: updatedSettings.showDesignStudioSubmitInstructions || false,
        designStudioSubmitInstructions: updatedSettings.designStudioSubmitInstructions || '',
        showLayoutCustomizationSubmitInstructions: updatedSettings.showLayoutCustomizationSubmitInstructions || false,
        layoutCustomizationSubmitInstructions: updatedSettings.layoutCustomizationSubmitInstructions || '',
        homeOwnersGuideProjects: updatedSettings.homeOwnersGuideProjects || [],
        colorSelectionProjects: updatedSettings.colorSelectionProjects || [],
        amenitiesButtonProjects: updatedSettings.amenitiesButtonProjects || [],
        projectProgressButtonProjects: updatedSettings.projectProgressButtonProjects || [],
        closingGuideButtonProjects: updatedSettings.closingGuideButtonProjects || [],
        // Clear file inputs after successful save
        headerLogo: null,
        loginPageLogo: null,
        loginPageHeroImage: null,
        bannerImage: null,
        incentiveImage: null,
      });

      // Clear the images to delete list
      setImagesToDelete(new Set());
    } catch (err: any) {
      console.error('Error saving portal settings:', err);
      toast.error(err.response?.data?.message || 'Failed to save portal settings');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'units', label: 'Units' },
    { id: 'parking', label: 'Parking' },
    { id: 'incentives', label: 'Incentives / Promotions' },
    { id: 'payments', label: 'Payments' },
    { id: 'others', label: 'Others' }
  ];

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="builders-container">
        {/* Section Header */}
        <div className="builders-section-header">
          <div>
            <h2 className="builders-section-title">Portal Settings</h2>
            <p className="builders-section-subtitle">
              Configure purchaser portal preferences and security settings
            </p>
          </div>
        </div>

        {/* Content */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
        }}>
          {loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="portal-settings-tabs">
                {tabs.map((tab) => {
                  return (
                    <button
                      key={tab.id}
                      className={`portal-settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab.id as any)}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* General Settings */}
              {activeTab === 'general' && (
                <div className="portal-settings-section" style={{ maxWidth: '100%' }}>
                  <h3 className="portal-settings-section-title">
                    Branding & Appearance
                  </h3>

                  {/* Row 1: Primary Color (1/3), Header Logo (1/3), Empty (1/3) */}
                  <div className="portal-settings-upload-grid-three">
                    {/* Primary Color */}
                    <div className="portal-settings-upload-item">
                      <label className="form-label">
                        Primary Color
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                        <div
                          className="portal-settings-color-preview"
                          style={{ backgroundColor: settings.primaryColor }}
                        />
                        <select
                          className="form-input"
                          value={settings.primaryColor}
                          onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                        >
                          <option value="#891F1E">#891F1E (Maroon)</option>
                          <option value="#1D222E">#1D222E (Dark Navy)</option>
                          <option value="#1565c0">#1565c0 (Blue)</option>
                          <option value="#424242">#424242 (Dark Gray)</option>
                          <option value="#673ab7">#673ab7 (Purple)</option>
                          <option value="#48182F">#48182F (Blackberry)</option>
                          <option value="#D4CBC4">#D4CBC4 (Moonstone)</option>
                        </select>
                      </div>
                    </div>

                    {/* Header Logo */}
                    <div className="portal-settings-upload-item">
                      <label className="form-label">Header Logo</label>
                      <div className="portal-settings-upload-container">
                        {settings.headerLogoUrl ? (
                          <div className="portal-settings-image-preview-wrapper">
                            <button
                              type="button"
                              className="portal-settings-delete-btn"
                              onClick={() => handleFileChange('headerLogo', null)}
                              title="Remove"
                            >
                              <X size={20} />
                            </button>
                            <img src={settings.headerLogoUrl} alt="Header Logo" className="portal-settings-preview-image" />
                          </div>
                        ) : (
                          <div className="portal-settings-upload-area">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileChange('headerLogo', e.target.files?.[0] || null)}
                              id="headerLogo"
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="headerLogo" className="portal-settings-upload-label">
                              Click to upload logo
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Empty Space */}
                    <div className="portal-settings-upload-item"></div>
                  </div>

                  {/* Row 2: Login Page Logo, Login Page Hero Image, Login Page Video */}
                  <div className="portal-settings-upload-grid-three">
                    {/* Login Page Logo */}
                    <div className="portal-settings-upload-item">
                      <label className="form-label">Login Page Logo</label>
                      <div className="portal-settings-upload-container">
                        {settings.loginPageLogoUrl ? (
                          <div className="portal-settings-image-preview-wrapper">
                            <button
                              type="button"
                              className="portal-settings-delete-btn"
                              onClick={() => handleFileChange('loginPageLogo', null)}
                              title="Remove"
                            >
                              <X size={20} />
                            </button>
                            <img src={settings.loginPageLogoUrl} alt="Login Page Logo" className="portal-settings-preview-image" />
                          </div>
                        ) : (
                          <div className="portal-settings-upload-area">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileChange('loginPageLogo', e.target.files?.[0] || null)}
                              id="loginPageLogo"
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="loginPageLogo" className="portal-settings-upload-label">
                              Click to upload logo
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Login Page Hero Image */}
                    <div className="portal-settings-upload-item">
                      <label className="form-label">Login Page Hero Image</label>
                      <div className="portal-settings-upload-container">
                        {settings.loginPageHeroImageUrl ? (
                          <div className="portal-settings-image-preview-wrapper">
                            <button
                              type="button"
                              className="portal-settings-delete-btn"
                              onClick={() => handleFileChange('loginPageHeroImage', null)}
                              title="Remove"
                            >
                              <X size={20} />
                            </button>
                            <img src={settings.loginPageHeroImageUrl} alt="Hero Image" className="portal-settings-preview-image" />
                          </div>
                        ) : (
                          <div className="portal-settings-upload-area">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileChange('loginPageHeroImage', e.target.files?.[0] || null)}
                              id="loginPageHeroImage"
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="loginPageHeroImage" className="portal-settings-upload-label">
                              Click to upload hero image
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Login Page Video */}
                    <div className="portal-settings-upload-item">
                      <label className="form-label">Login Page Video</label>
                      <div className="portal-settings-upload-container">
                        {settings.loginPageVideoUrl && settings.loginPageVideoType ? (
                          <div className="portal-settings-image-preview-wrapper">
                            <button
                              type="button"
                              className="portal-settings-delete-btn"
                              onClick={handleDeleteVideo}
                              disabled={saving}
                              title="Remove"
                            >
                              <X size={20} />
                            </button>
                            <div style={{ width: '100%', height: '250px', borderRadius: '8px', overflow: 'hidden' }}>
                              <VideoPlayer
                                videoUrl={settings.loginPageVideoUrl}
                                videoType={settings.loginPageVideoType}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="portal-settings-upload-area">
                            <button
                              type="button"
                              className="portal-settings-upload-label"
                              onClick={() => setIsVideoModalOpen(true)}
                            >
                              Add Login Page Video
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Row 3: Intro Banner Image & Intro Text (1/3 and 2/3) */}
                  <div className="portal-settings-upload-grid-custom">
                    {/* Intro Banner Image */}
                    <div className="portal-settings-upload-item">
                      <label className="form-label">Intro Banner Image</label>
                      <div className="portal-settings-upload-container">
                        {settings.bannerImageUrl ? (
                          <div className="portal-settings-image-preview-wrapper">
                            <button
                              type="button"
                              className="portal-settings-delete-btn"
                              onClick={() => handleFileChange('bannerImage', null)}
                              title="Remove"
                            >
                              <X size={20} />
                            </button>
                            <img src={settings.bannerImageUrl} alt="Intro Banner" className="portal-settings-preview-image" />
                          </div>
                        ) : (
                          <div className="portal-settings-upload-area">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileChange('bannerImage', e.target.files?.[0] || null)}
                              id="bannerImage"
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="bannerImage" className="portal-settings-upload-label">
                              Click to upload
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Intro Text */}
                    <div className="portal-settings-upload-item">
                      <label className="form-label">
                        Intro Text
                      </label>
                      <div className="portal-settings-richtext-container">
                        <RichTextEditor
                          content={settings.introText}
                          onChange={(content) => setSettings({ ...settings, introText: content })}
                          placeholder="Enter introductory text for the login page"
                        />
                      </div>
                    </div>
                  </div>

                  <h3 className="portal-settings-section-title" style={{ marginTop: '32px' }}>
                    Content Settings
                  </h3>

                  {/* Extra Information Title */}
                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Welcome Title
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={settings.extraInfoTitle}
                      onChange={(e) => setSettings({ ...settings, extraInfoTitle: e.target.value })}
                      placeholder="Enter welcome title"
                    />
                  </div>

                  {/* Extra Information Body */}
                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Welcome Message
                    </label>
                    <RichTextEditor
                      content={settings.extraInfoBody}
                      onChange={(content) => setSettings({ ...settings, extraInfoBody: content })}
                      placeholder="Enter welcome message"
                    />
                  </div>

                  {/* Help Text */}
                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Help Text
                    </label>
                    <RichTextEditor
                      content={settings.helpText}
                      onChange={(content) => setSettings({ ...settings, helpText: content })}
                      placeholder="Enter help and contact information"
                    />
                  </div>

                  {/* Image Disclaimer */}
                  <h3 className="portal-settings-section-title" style={{ marginTop: '32px' }}>
                    Image Disclaimer
                  </h3>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.showImageDisclaimer}
                        onChange={(e) => setSettings({ ...settings, showImageDisclaimer: e.target.checked })}
                      />
                      <span>Show Image Disclaimer</span>
                    </label>
                  </div>

                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      INSTRUCTIONS :
                    </label>
                    <RichTextEditor
                      content={settings.imageDisclaimerText}
                      onChange={(content) => setSettings({ ...settings, imageDisclaimerText: content })}
                      placeholder=""
                    />
                  </div>

                  {/* Save Button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
                    <button
                      className="btn btn-primary btn-md"
                      onClick={handleSave}
                      disabled={saving}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Save size={18} />
                      {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}

              {/* Units Settings */}
              {activeTab === 'units' && (
                <div className="portal-settings-section">
                  {/* Unit Display Attribute */}
                  <h3 className="portal-settings-section-title">
                    Unit Display Attribute
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.model}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, model: e.target.checked } })}
                      />
                      <span>Model</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.collection}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, collection: e.target.checked } })}
                      />
                      <span>Collection</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.unitType}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, unitType: e.target.checked } })}
                      />
                      <span>Type</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.bathroom}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, bathroom: e.target.checked } })}
                      />
                      <span>Bathroom</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.bedroom}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, bedroom: e.target.checked } })}
                      />
                      <span>Bedroom</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.den}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, den: e.target.checked } })}
                      />
                      <span>Den</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.media}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, media: e.target.checked } })}
                      />
                      <span>Media</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.flex}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, flex: e.target.checked } })}
                      />
                      <span>Flex</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.area}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, area: e.target.checked } })}
                      />
                      <span>Area</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.ceiling}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, ceiling: e.target.checked } })}
                      />
                      <span>Ceiling</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.outdoorArea}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, outdoorArea: e.target.checked } })}
                      />
                      <span>Outdoor Area</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.outdoorType}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, outdoorType: e.target.checked } })}
                      />
                      <span>Outdoor Type</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.parking}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, parking: e.target.checked } })}
                      />
                      <span>Parking</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.locker}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, locker: e.target.checked } })}
                      />
                      <span>Locker</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitDisplay.bicycle}
                        onChange={(e) => setSettings({ ...settings, unitDisplay: { ...settings.unitDisplay, bicycle: e.target.checked } })}
                      />
                      <span>Bicycle</span>
                    </label>
                  </div>

                  {/* Unit Display Options */}
                  <h3 className="portal-settings-section-title" style={{ marginTop: '32px' }}>
                    Unit Display Options
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitOption.floorLayout}
                        onChange={(e) => setSettings({ ...settings, unitOption: { ...settings.unitOption, floorLayout: e.target.checked } })}
                      />
                      <span>Floor Layout</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitOption.keyMap}
                        onChange={(e) => setSettings({ ...settings, unitOption: { ...settings.unitOption, keyMap: e.target.checked } })}
                      />
                      <span>Key Map</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitOption.electricalLayout}
                        onChange={(e) => setSettings({ ...settings, unitOption: { ...settings.unitOption, electricalLayout: e.target.checked } })}
                      />
                      <span>Electrical Layout</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitOption.suiteLayout}
                        onChange={(e) => setSettings({ ...settings, unitOption: { ...settings.unitOption, suiteLayout: e.target.checked } })}
                      />
                      <span>Suite Layout</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitOption.photos}
                        onChange={(e) => setSettings({ ...settings, unitOption: { ...settings.unitOption, photos: e.target.checked } })}
                      />
                      <span>Photos</span>
                    </label>
                  </div>

                  {/* Display Unit Attributes in Summary Page */}
                  <h3 className="portal-settings-section-title" style={{ marginTop: '32px' }}>
                    Display Unit Attributes in Summary Page
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.model}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, model: e.target.checked } })}
                      />
                      <span>Model</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.collection}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, collection: e.target.checked } })}
                      />
                      <span>Collection</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.unitType}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, unitType: e.target.checked } })}
                      />
                      <span>Type</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.bathroom}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, bathroom: e.target.checked } })}
                      />
                      <span>Bathroom</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.bedroom}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, bedroom: e.target.checked } })}
                      />
                      <span>Bedroom</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.den}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, den: e.target.checked } })}
                      />
                      <span>Den</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.media}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, media: e.target.checked } })}
                      />
                      <span>Media</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.flex}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, flex: e.target.checked } })}
                      />
                      <span>Flex</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.area}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, area: e.target.checked } })}
                      />
                      <span>Area</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.ceiling}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, ceiling: e.target.checked } })}
                      />
                      <span>Ceiling</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.outdoorArea}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, outdoorArea: e.target.checked } })}
                      />
                      <span>Outdoor Area</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.outdoorType}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, outdoorType: e.target.checked } })}
                      />
                      <span>Outdoor Type</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.floorMarketing}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, floorMarketing: e.target.checked } })}
                      />
                      <span>Floor Marketing</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.unitSummary.views}
                        onChange={(e) => setSettings({ ...settings, unitSummary: { ...settings.unitSummary, views: e.target.checked } })}
                      />
                      <span>Views</span>
                    </label>
                  </div>

                  {/* Save Button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
                    <button
                      className="btn btn-primary btn-md"
                      onClick={handleSave}
                      disabled={saving}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Save size={18} />
                      {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}

              {/* Parking Settings */}
              {activeTab === 'parking' && (
                <div className="portal-settings-section">
                  <h3 className="portal-settings-section-title">
                    Parking Settings
                  </h3>

                  {/* Selection Time Limit */}
                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Selection Time Limit (minute) :
                    </label>
                    <input
                      type="number"
                      className="form-input"
                      value={settings.parkingSelectionTimeLimit}
                      onChange={(e) => setSettings({ ...settings, parkingSelectionTimeLimit: parseInt(e.target.value) || 0 })}
                      placeholder="Enter time limit in minutes"
                      min="0"
                      style={{ maxWidth: '200px' }}
                    />
                  </div>

                  {/* Parking Info Text */}
                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Parking Info Text :
                    </label>
                    <RichTextEditor
                      content={settings.parkingInfoText}
                      onChange={(content) => setSettings({ ...settings, parkingInfoText: content })}
                      placeholder="Enter parking information text"
                    />
                  </div>

                  {/* Locker Info Text */}
                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Locker Info Text :
                    </label>
                    <RichTextEditor
                      content={settings.lockerInfoText}
                      onChange={(content) => setSettings({ ...settings, lockerInfoText: content })}
                      placeholder="Enter locker information text"
                    />
                  </div>

                  {/* Bicycle Info Text */}
                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Bicycle Info Text :
                    </label>
                    <RichTextEditor
                      content={settings.bicycleInfoText}
                      onChange={(content) => setSettings({ ...settings, bicycleInfoText: content })}
                      placeholder="Enter bicycle information text"
                    />
                  </div>

                  {/* Save Button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
                    <button
                      className="btn btn-primary btn-md"
                      onClick={handleSave}
                      disabled={saving}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Save size={18} />
                      {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}

              {/* Incentives / Promotions Settings */}
              {activeTab === 'incentives' && (
                <div className="portal-settings-section">
                  <h3 className="portal-settings-section-title">
                    Incentives / Promotions
                  </h3>

                  {/* Display Options Checkboxes */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '12px' }}>
                      <input
                        type="checkbox"
                        checked={settings.displayIncentivesOnHomePage}
                        onChange={(e) => setSettings({ ...settings, displayIncentivesOnHomePage: e.target.checked })}
                      />
                      <span>Display Incentives on Home Page</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.displayIncentivesOnUnitDetails}
                        onChange={(e) => setSettings({ ...settings, displayIncentivesOnUnitDetails: e.target.checked })}
                      />
                      <span>Display Incentives on Unit Details</span>
                    </label>
                  </div>

                  {/* Incentive Title */}
                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Incentive Title :
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={settings.incentiveTitle}
                      onChange={(e) => setSettings({ ...settings, incentiveTitle: e.target.value })}
                      placeholder="Enter incentive title"
                    />
                  </div>

                  {/* Incentive Body */}
                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Incentive Body :
                    </label>
                    <RichTextEditor
                      content={settings.incentiveBody}
                      onChange={(content) => setSettings({ ...settings, incentiveBody: content })}
                      placeholder="Enter incentive body"
                    />
                  </div>

                  {/* Incentive Image */}
                  <div className="portal-settings-upload-grid-three" style={{ marginTop: '24px' }}>
                    <div className="portal-settings-upload-item">
                      <label className="form-label">Incentive Image</label>
                      <div className="portal-settings-upload-container">
                        {settings.incentiveImageUrl || settings.incentiveImage ? (
                          <div className="portal-settings-image-preview-wrapper">
                            <button
                              type="button"
                              className="portal-settings-delete-btn"
                              onClick={() => handleFileChange('incentiveImage', null)}
                              title="Remove"
                            >
                              <X size={20} />
                            </button>
                            <img
                              src={
                                settings.incentiveImage
                                  ? URL.createObjectURL(settings.incentiveImage)
                                  : settings.incentiveImageUrl
                              }
                              alt="Incentive"
                              className="portal-settings-preview-image"
                            />
                          </div>
                        ) : (
                          <div className="portal-settings-upload-area">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileChange('incentiveImage', e.target.files?.[0] || null)}
                              id="incentiveImage"
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="incentiveImage" className="portal-settings-upload-label">
                              Click to upload image
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
                    <button
                      className="btn btn-primary btn-md"
                      onClick={handleSave}
                      disabled={saving}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Save size={18} />
                      {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}

              {/* Payments Settings */}
              {activeTab === 'payments' && (
                <div className="portal-settings-section">
                  <h3 className="portal-settings-section-title">
                    Payment Instructions
                  </h3>

                  {/* Show Payment Instructions Checkbox */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.showPaymentInstructions}
                        onChange={(e) => setSettings({ ...settings, showPaymentInstructions: e.target.checked })}
                      />
                      <span>Show Payment Instructions</span>
                    </label>
                  </div>

                  {/* Instructions */}
                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Instructions :
                    </label>
                    <RichTextEditor
                      content={settings.paymentInstructions}
                      onChange={(content) => setSettings({ ...settings, paymentInstructions: content })}
                      placeholder="Enter payment instructions"
                    />
                  </div>

                  {/* Save Button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
                    <button
                      className="btn btn-primary btn-md"
                      onClick={handleSave}
                      disabled={saving}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Save size={18} />
                      {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}

              {/* Others Settings */}
              {activeTab === 'others' && (
                <div className="portal-settings-section">
                  {/* Design Studio Submit Instructions */}
                  <h3 className="portal-settings-section-title">
                    Design Studio Submit Instructions
                  </h3>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.showDesignStudioSubmitInstructions}
                        onChange={(e) => setSettings({ ...settings, showDesignStudioSubmitInstructions: e.target.checked })}
                      />
                      <span>Show Submit Instructions</span>
                    </label>
                  </div>

                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Instructions :
                    </label>
                    <RichTextEditor
                      content={settings.designStudioSubmitInstructions}
                      onChange={(content) => setSettings({ ...settings, designStudioSubmitInstructions: content })}
                      placeholder=""
                    />
                  </div>

                  {/* Layout Customization Instructions */}
                  <h3 className="portal-settings-section-title" style={{ marginTop: '32px' }}>
                    Layout Customization Instructions
                  </h3>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.showLayoutCustomizationSubmitInstructions}
                        onChange={(e) => setSettings({ ...settings, showLayoutCustomizationSubmitInstructions: e.target.checked })}
                      />
                      <span>Show Submit Instructions</span>
                    </label>
                  </div>

                  <div className="portal-settings-form-group">
                    <label className="form-label">
                      Instructions :
                    </label>
                    <RichTextEditor
                      content={settings.layoutCustomizationSubmitInstructions}
                      onChange={(content) => setSettings({ ...settings, layoutCustomizationSubmitInstructions: content })}
                      placeholder=""
                    />
                  </div>

                  {/* Show/Hide Home Owners Guide Button */}
                  <h3 className="portal-settings-section-title" style={{ marginTop: '32px' }}>
                    Show Home Owners Guide Button
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                    {projects.map((project) => (
                      <label key={project._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={settings.homeOwnersGuideProjects.includes(project._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSettings({
                                ...settings,
                                homeOwnersGuideProjects: [...settings.homeOwnersGuideProjects, project._id]
                              });
                            } else {
                              setSettings({
                                ...settings,
                                homeOwnersGuideProjects: settings.homeOwnersGuideProjects.filter(id => id !== project._id)
                              });
                            }
                          }}
                        />
                        <span>{project.name}</span>
                      </label>
                    ))}
                  </div>

                  {/* Color Selection Submit Button */}
                  <h3 className="portal-settings-section-title" style={{ marginTop: '32px' }}>
                    Show Color Selection Submit Button
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                    {projects.map((project) => (
                      <label key={project._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={settings.colorSelectionProjects.includes(project._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSettings({
                                ...settings,
                                colorSelectionProjects: [...settings.colorSelectionProjects, project._id]
                              });
                            } else {
                              setSettings({
                                ...settings,
                                colorSelectionProjects: settings.colorSelectionProjects.filter(id => id !== project._id)
                              });
                            }
                          }}
                        />
                        <span>{project.name}</span>
                      </label>
                    ))}
                  </div>

                  {/* Show Amenities Button */}
                  <h3 className="portal-settings-section-title" style={{ marginTop: '32px' }}>
                    Show Amenities Button
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                    {projects.map((project) => (
                      <label key={project._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={settings.amenitiesButtonProjects.includes(project._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSettings({
                                ...settings,
                                amenitiesButtonProjects: [...settings.amenitiesButtonProjects, project._id]
                              });
                            } else {
                              setSettings({
                                ...settings,
                                amenitiesButtonProjects: settings.amenitiesButtonProjects.filter((id: string) => id !== project._id)
                              });
                            }
                          }}
                        />
                        <span>{project.name}</span>
                      </label>
                    ))}
                  </div>

                  {/* Show Project Progress Button */}
                  <h3 className="portal-settings-section-title" style={{ marginTop: '32px' }}>
                    Show Project Progress Button
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                    {projects.map((project) => (
                      <label key={project._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={settings.projectProgressButtonProjects.includes(project._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSettings({
                                ...settings,
                                projectProgressButtonProjects: [...settings.projectProgressButtonProjects, project._id]
                              });
                            } else {
                              setSettings({
                                ...settings,
                                projectProgressButtonProjects: settings.projectProgressButtonProjects.filter((id: string) => id !== project._id)
                              });
                            }
                          }}
                        />
                        <span>{project.name}</span>
                      </label>
                    ))}
                  </div>

                  {/* Show Closing Guide Button */}
                  <h3 className="portal-settings-section-title" style={{ marginTop: '32px' }}>
                    Show Closing Guide Button
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                    {projects.map((project) => {
                      const closingGuideEntry = settings.closingGuideButtonProjects.find(
                        (entry: { projectId: string; url: string }) => entry.projectId === project._id
                      );
                      const isChecked = !!closingGuideEntry;
                      const currentUrl = closingGuideEntry?.url || '';

                      return (
                        <div key={project._id} style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSettings({
                                    ...settings,
                                    closingGuideButtonProjects: [
                                      ...settings.closingGuideButtonProjects,
                                      { projectId: project._id, url: '' }
                                    ]
                                  });
                                } else {
                                  setSettings({
                                    ...settings,
                                    closingGuideButtonProjects: settings.closingGuideButtonProjects.filter(
                                      (entry: { projectId: string; url: string }) => entry.projectId !== project._id
                                    )
                                  });
                                }
                              }}
                            />
                            <span style={{ fontWeight: 500 }}>{project.name}</span>
                          </label>
                          {isChecked && (
                            <div style={{ marginLeft: '28px' }}>
                              <label className="form-label" style={{ fontSize: '14px', marginBottom: '4px' }}>
                                Closing Guide URL:
                              </label>
                              <input
                                type="url"
                                className="form-input"
                                placeholder="Enter closing guide URL (e.g., https://example.com/guide.pdf)"
                                value={currentUrl}
                                onChange={(e) => {
                                  setSettings({
                                    ...settings,
                                    closingGuideButtonProjects: settings.closingGuideButtonProjects.map((entry: { projectId: string; url: string }) =>
                                      entry.projectId === project._id
                                        ? { ...entry, url: e.target.value }
                                        : entry
                                    )
                                  });
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Save Button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
                    <button
                      className="btn btn-primary btn-md"
                      onClick={handleSave}
                      disabled={saving}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Save size={18} />
                      {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Video Upload Modal */}
      {workspaceId && (
        <VideoUploadModal
          isOpen={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          onVideoUploaded={(videoUrl, videoType) => {
            // For uploaded MP4 files, we get the S3 URL
            // For URL-based videos, we get the YouTube/Vimeo URL
            // In both cases, store the URL and type for preview
            setSettings({
              ...settings,
              loginPageVideoUrl: videoUrl,
              loginPageVideoType: videoType
            });
          }}
          workspaceId={workspaceId}
          projectId={workspaceId}
          currentVideoUrl={settings.loginPageVideoUrl}
        />
      )}
    </AppLayout>
  );
};

export default PortalSettings;
